import type { InterviewInviteListItemDtoStatus } from '@sara/api-client';
import { Badge, type BadgeProps } from '@sara/ui/components/badge';

type BadgeTone = NonNullable<BadgeProps['variant']>;

interface StatusMeta {
  label: string;
  tone: BadgeTone;
  /** Whether the leading status dot should pulse (active/live states). */
  pulse?: boolean;
}

const STATUS_META: Record<InterviewInviteListItemDtoStatus, StatusMeta> = {
  PENDING: { label: 'Pending', tone: 'warning' },
  IN_PROGRESS: { label: 'In progress', tone: 'info', pulse: true },
  COMPLETED: { label: 'Completed', tone: 'success' },
  PAUSED: { label: 'Paused', tone: 'warning' },
  EXHAUSTED: { label: 'Tries exhausted', tone: 'neutral' },
  ERROR: { label: 'Error', tone: 'danger' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
  LIVE_INTERVIEW_REQUESTED: { label: 'Live interview requested', tone: 'info' },
};

// Visual override: PENDING reads as "soft warning" (awaiting action, not urgent).
const TONE_CLASS_OVERRIDES: Partial<Record<InterviewInviteListItemDtoStatus, string>> = {
  PENDING: 'bg-warning-muted/60',
};

interface InterviewStatusBadgeProps {
  status: InterviewInviteListItemDtoStatus;
}

export function InterviewStatusBadge({ status }: InterviewStatusBadgeProps) {
  const { label, tone, pulse } = STATUS_META[status];
  const override = TONE_CLASS_OVERRIDES[status];

  return (
    <Badge variant={tone} className={override} aria-label={`Status: ${label}`}>
      <StatusDot pulse={pulse} />
      {label}
    </Badge>
  );
}

function StatusDot({ pulse }: { pulse?: boolean }) {
  return (
    <span className="relative inline-flex h-1.5 w-1.5" aria-hidden="true">
      {pulse && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
      )}
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
    </span>
  );
}
